<template>
	<q-field :helper="$t(getHelper)" :error="error" :warning="showDisableWarning" :warning-label="$t('not_allowed_to_edit_the_class_file')">
		<q-uploader auto-expand hide-upload-button inverted ref="uploader" color="primary" url="" :multiple="multiple"
			:extensions="getExtensions" :upload-factory="uploadFile" @add="fileAdded" @remove:abort="uploadCancel" :filter="filterFiles"
			@remove:cancel="uploadCancel" :disable="disable" :float-label="imageSize" />

		<q-modal v-model="crop_modal"
			:content-css="{ maxWidth: getMaxWidth, minWidth: getMaxWidth, minHeight: '80vh' }">
			<q-modal-layout>
				<q-toolbar slot="header">
					<q-btn flat round dense @click="canceledCrop()" icon="fas fa-times" />
					<q-toolbar-title>
						{{ $t('crop_image') }}
					</q-toolbar-title>
				</q-toolbar>

				<div class="layout-padding">
					<vue-cropper ref="cropper" :src="original_image" :guides="true" drag-mode="crop" :view-mode="1"
						:aspect-ratio="aspect_ratio ? aspect_ratio : 16 / 9" :background="true"
						:img-style="{ width: 'auto', height: '500px' }" />

					<div class="row q-mt-sm gutter-sm justify-end">
						<div class="col-xs-12 col-sm-4" :class="getOrder('cancel')">
							<q-btn color="tertiary" class="full-width" :label="$t('cancel')" @click="canceledCrop()" />
						</div>

						<div class="col-xs-12 col-sm-4" :class="getOrder('crop')">
							<q-btn color="primary" class="full-width" :label="$t('crop')" @click="cropImage()" />
						</div>
					</div>
				</div>
			</q-modal-layout>
		</q-modal>
	</q-field>
</template>

<script>
import { mapGetters } from 'vuex'
import { BlobServiceClient } from '@azure/storage-blob'
import VueCropper from 'vue-cropperjs'

export default {
	i18nOptions: { namespaces: ['custom', 'common', 'admin'] },

	props: [
		'content_type',
		'multiple',
		'cropper',
		'error',
		'item',
		'custom_directory',
		'custom_certificate_module_path',
		'filename',
		'slug',
		'client_id',
		'file',
		'aspect_ratio',
		'disable',
		'show_disable_warning',
		'aspect_ratio_string',
		'id',
		'guest_import'
	],

	data() {
		return {
			upload_url: process.env.API_URL + '/files/policy/',
			urls: [],
			cancel_token: '',
			source: '',
			crop_modal: false,
			original_image: '',
		}
	},

	components: {
		VueCropper,
	},

	computed: {
		...mapGetters({
			access_token: 'common/access_token',
			is_superuser: 'common/is_superuser',
		}),

		getHeader() {
			let headers = {}
			return (headers = {
				headers: {
					'Content-Type': 'multipart/form-data',
					authorization:
						'Basic ' +
						btoa(process.env.APP_CLIENT_ID + ':' + process.env.APP_CLIENT_SECRET),
					authorization: 'Bearer ' + this.access_token,
				},
			})
		},

		showDisableWarning() {
			return this.disable && this.show_disable_warning !== false
		},

		getExtensions() {
			switch (this.content_type) {
				case 'images':
					return 'image/jpg,image/jpeg,image/png,.jpg,.jpeg,.png,.svg'
				case 'video':
					return 'video/mp4,.mp4'
				case 'pdf':
					return 'application/pdf,.pdf'
				case 'scorm':
					return 'zip'
				case 'audio':
					return 'audio/mpeg,audio/ogg,.mp3,.ogg'
				case 'html':
					return 'zip'
				case 'xlsx':
					return 'application/vnd.ms-excel,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet,.xls,.xlsx'
				case 'ppt':
					return 'application/vnd.openxmlformats-officedocument.presentationml.presentation,application/vnd.ms-powerpoint,.ppt,.pptx'
				default:
					return ''
			}
		},

		getHelper() {
			switch (this.content_type) {
				case 'images':
					return 'helper_uploader_images'
				case 'video':
					return 'helper_uploader_videos'
				case 'pdf':
					return 'helper_uploader_presentation'
				case 'scorm':
					return 'helper_uploader_scorm'
				case 'audio':
					return 'helper_uploader_audio'
				case 'html':
					return 'helper_uploader_html'
				case 'xlsx':
					return 'helper_uploader_xlsx'
				case 'ppt':
					return 'helper_uploader_ppt'
				default:
					return 'helper_uploader_file'
			}
		},

		imageSize() {
			if (this.content_type === 'images') {
				let size = this.getCroppedSize
				return `${this.$t('image_size')}: ${size.width}px / ${size.height}px`
			} else return this.$t('select_a_file')
		},

		getMaxWidth() {
			if (this.$q.platform.is.mobile) return '100vw'
			else return '80vw'
		},

		getCroppedSize() {
			if (this.aspect_ratio_string) {
				switch (this.aspect_ratio_string) {
					case '1080/1927':
						return {
							width: 1080,
							height: 1927,
						}
					case '16/9':
						return {
							width: 1280,
							height: 720,
						}
					case '192/40':
						return {
							width: 1920,
							height: 400,
						}
					case '1/1':
						return {
							width: 250,
							height: 250,
						}
					case '1200/457':
						return {
							width: 1200,
							height: 457,
						}
					case '790/520':
						return {
							width: 790,
							height: 520,
						}
					case '100/97':
						return {
							width: 500,
							height: 485,
						}
					case '128/25':
						return {
							width: 1280,
							height: 250,
						}
					case '5/3':
						return {
							width: 500,
							height: 300,
						}
					case '600/334':
						return {
							width: 600,
							height: 334,
						}
					case '395/100':
						return {
							width: 395,
							height: 100,
						}
					case '395/47':
						return {
							width: 395,
							height: 47,
						}
					case '600/338':
						return {
							width: 600,
							height: 338,
						}
					case '830/580':
						return {
							width: 830,
							height: 580,
						}
					case '512/512':
						return {
							width: 512,
							height: 512,
						}
					case '600/400':
						return {
							width: 600,
							height: 400,
						}
					case '650/650':
						return {
							width: 650,
							height: 650,
						}
					case '1600/340':
						return {
							width: 1600,
							height: 340,
						}
					case '984/450':
						return {
							width: 984,
							height: 450,
						}
					case '600/317':
						return {
							width: 600,
							height: 317,
						}
					case '1366/320':
						return {
							width: 1366,
							height: 320,
						}
					case '52/29':
						return {
							width: 520,
							height: 290,
						}
					case '140/113':
						return {
							width: 840,
							height: 678,
						}
					case '877/620':
						return {
							width: 1754,
							height: 1240,
						}
					case '500/485':
						return {
							width: 500,
							height: 485,
						}
					case '343/200':
						return {
							width: 343,
							height: 200,
						}
					default:
						return {
							width: 1280,
							height: 720,
						}
				}
			} else
				return {
					width: 1280,
					height: 720,
				}
		},
	},

	methods: {
		fileAdded(file) {
			this.$refs.uploader.upload()
		},

		filterFiles (files) {
			const filtered = files.filter(file => {
				 if (this.is_superuser) {
					return true
				}
				if (
					(file.size > 150000000 && this.content_type !== 'video') ||
					(file.size > 1000000000 && this.content_type === 'video')
				) {
					this.$q.notify({
						message: this.content_type === 'video' ? this.$t('file_too_large_max_size_1gb') : this.$t('file_too_large_max_size_150mb'),
						type: 'negative',
						icon: 'fal fa-ban'
					})
					return false
				}
				return true 
			});

			return filtered;
    },

		uploadCancel() {
			if (this.source) this.source.cancel()
		},

		showCropper(file) {
			if (file.cropped) {
				return
			}

			const reader = new FileReader()
			reader.onload = (event) => {
				this.original_image = event.target.result
				this.$refs.cropper.replace(event.target.result)
			}
			reader.readAsDataURL(file)
			this.crop_modal = true
		},

		cropImage() {
			this.$refs.uploader.reset()

			var blob = this.$refs.cropper
				.getCroppedCanvas(this.getCroppedSize)
				.toDataURL('jpeg', '1')
			var newFile = this.dataURItoBlob(blob)
			newFile.cropped = true
			newFile.name =
				Math.random()
					.toString(36)
					.substr(2, 9) + '.jpg'

			this.crop_modal = false
			this.$refs.uploader.add([newFile])
			this.$refs.uploader.upload()
		},

		canceledCrop() {
			this.$refs.uploader.reset()
			this.crop_modal = false
		},

		uploadFile(file, updateProgress) {
			if (!this.cropper || file.cropped) {
				return new Promise((resolve) => {
					let fd = new FormData()
					fd.append(
						'filename',
						this.filename ?
							`${this.filename}.${file.name.split('.')[file.name.split('.').length - 1]}` :
							file.name
					)

					if (this.custom_certificate_module_path) {
						fd.append(
							'file_directory',
							`media_public/${this.custom_certificate_module_path}/${this.id}/images/${this.filename
							}/`
						)
						fd.append('keep_file_name', true)
					} else if (this.custom_directory) {
						if (this.client_id)
							fd.append(
								'file_directory',
								`media_public/configs/${this.slug}/${this.client_id}/${this.filename
								}/`
							)
						else
							fd.append(
								'file_directory',
								`media_public/configs/${this.slug}/${this.filename}/`
							)

						fd.append('bucket_name', 'static')

						fd.append('keep_file_name', true)
					}

					this.$q.loading.show()

					this.$axios
						.post('/api/files/policy/', fd, this.getHeader, {
							cancelToken: this.source.token,
						})
						.then((response) => {
							if (response.data.origin === 'azure') {
								const blobServiceClient = new BlobServiceClient(
									`${response.data.base_url}?${response.data.sas_token}`
								)

								const main = async () => {
									const container_client = blobServiceClient.getContainerClient(
										''
									)

									const block_blob_client = container_client.getBlockBlobClient(
										response.data.filename
									)
									const upload_blob_response = await block_blob_client.uploadBrowserData(
										file
									)

									let fd_complete = new FormData()
									fd_complete.append('uploaded', true)
									fd_complete.append('fileSize', file.size)
									fd_complete.append('file', response.data.file_id)

									if (this.custom_directory) {
										fd_complete.append('not_signed', true)
										fd_complete.append('bucket_name', 'static')
									}

									this.$axios
										.post('/api/files/complete/', fd_complete, this.getHeader)
										.then((response) => {
											this.$refs.uploader.reset()

											let item = null
											if (this.item) item = this.item
											this.$emit('fileUploaded', response.data.fileUrl, item)
										})
										.finally(() => {
											resolve()
											this.$q.loading.hide()
										})
								}

								main()
							} else {
								let content_type =
									file.type != '' ? file.type : 'application/octet-stream'
								let policy = response.data
								let file_name = policy.filename
								let key_path = policy.file_bucket_path

								let fd_s3 = new FormData()
								fd_s3.append('key', key_path + file_name)
								fd_s3.append('acl', 'private')
								fd_s3.append('Content-Type', content_type)
								fd_s3.append('AWSAccessKeyId', policy.key)
								fd_s3.append('Policy', policy.policy)
								fd_s3.append('filename', file_name)
								fd_s3.append('Signature', policy.signature)
								fd_s3.append('file', file)

								this.cancel_token = this.$axios.CancelToken
								this.source = this.cancel_token.source()

								this.$axios
									.post(process.env.BUCKET_URL, fd_s3, {
										onUploadProgress: function (event) {
											updateProgress(event.loaded / event.total)
										},
										cancelToken: this.source.token,
									})
									.then((response) => {
										let fd_complete = new FormData()
										fd_complete.append('uploaded', true)
										fd_complete.append('fileSize', file.size)
										fd_complete.append('file', policy.file_id)

										this.$axios
											.post(
												'/api/files/complete/',
												fd_complete,
												this.getHeader
											)
											.then((response) => {
												this.$refs.uploader.reset()
												const url = response.data.fileUrlSigned
												const cleanUrl = url.split('?')[0]
												let item = null
												if (this.item) item = this.item
												this.$emit(
													'fileUploaded',
													cleanUrl ? cleanUrl : '',
													item,
													response.data.fileUrlSigned
												)
												if (this.guest_import) {
													this.$q.notify({
														message: this.$t('guest_import_success'),
														type: 'positive',
														icon: 'fal fa-save',
													})
												}
											})
											
											
									})
									.finally(() => {
										resolve()
									})
							}
						})
						.catch((e) => {
							resolve()
							this.$refs.uploader.reset()
							this.$q.notify({
								message: this.$t('save_error'),
								type: 'negative',
								icon: 'fal fa-ban'
							})
							console.log(e)
						})
						.finally(() => {
							this.$q.loading.hide()
						})
				})
			} else {
				this.showCropper(file)
			}
		},

		getOrder(name) {
			if (name == 'cancel') {
				if (this.$q.platform.is.mobile) return 'order-last'
				else return 'order-first'
			} else if (name == 'crop') {
				if (this.$q.platform.is.mobile) return 'order-first'
				else return 'order-last'
			}
		},

		dataURItoBlob(dataURI) {
			var byteString = atob(dataURI.split(',')[1])
			var ab = new ArrayBuffer(byteString.length)
			var ia = new Uint8Array(ab)
			for (var i = 0; i < byteString.length; i++) {
				ia[i] = byteString.charCodeAt(i)
			}
			return new Blob([ab], { type: 'image/jpeg' })
		},
	},

	watch: {
		file: function () {
			if (this.file) this.showCropper(this.file)
		},
	},
}
</script>

<style lang="stylus" scoped></style>
